import { useMemo, useState } from "react";

import { ArrowDownward, ArrowUpward } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import PropTypes from "prop-types";

import { CustomDialogTitle, CustomInputField, CustomSelect } from "@/features/ui";

import {
  canMoveColumn,
  cloneLayout,
  getOrderedColumns,
  MARGIN_FIELDS,
  moveLayoutColumn,
  updateLayoutColumn,
  validateLayout,
} from "../utils/report-layout";

const BLOCK_TITLES = {
  metadata: "Informasi Barang",
  opening: "Saldo Awal",
  in: "Masuk",
  out: "Keluar",
  closing: "Saldo Akhir",
};
const FONT_FAMILY_LABELS = { sans: "Sans-serif", serif: "Serif", mono: "Monospace" };
const DENSITY_LABELS = { compact: "Ringkas", normal: "Normal" };
const HEADER_THEME_LABELS = { blue: "Biru", gray: "Abu-abu", plain: "Polos" };
const MONEY_DECIMAL_LABELS = { 0: "Tanpa desimal", 2: "2 desimal" };
const NEGATIVE_FORMAT_LABELS = { minus: "Minus (-1.000)", parentheses: "Kurung ((1.000))" };
const ORIENTATION_LABELS = { portrait: "Tegak", landscape: "Mendatar" };
const formatMm = (value) => String(value).replace(".", ",");
const paperOptions = ({ paperSizes, paperDimensionsMm = [] }) =>
  paperSizes.map((paper) => {
    const size = paperDimensionsMm.find((entry) => entry.paper === paper);
    return {
      value: paper,
      label: size
        ? `${paper} (${formatMm(size.short)} × ${formatMm(size.long)} mm)`
        : paper,
    };
  });
const SORT_FIELD_LABELS = {
  sku: "Kode Barang",
  name: "Nama Barang",
  closingQty: "Kuantitas Saldo Akhir",
  closingValue: "Nilai Saldo Akhir",
};
const SORT_DIRECTION_LABELS = { asc: "naik", desc: "turun" };
const DEFAULT_SORT = { field: "sku", direction: "asc" };

const sortOptions = ({ sortFields, sortDirections }) =>
  sortFields.flatMap((field) =>
    sortDirections.map((direction) => ({
      value: `${field}:${direction}`,
      label: `${SORT_FIELD_LABELS[field] ?? field} (${
        SORT_DIRECTION_LABELS[direction] ?? direction
      })`,
    }))
  );

// One aligned row per column: visibility, label, width and order; captions are shown once.
const COLUMN_GRID_SX = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "minmax(140px, 1fr) 2fr 130px auto" },
  columnGap: 2,
  rowGap: 1,
  alignItems: "center",
};

const toInteger = (value) => (value === "" ? Number.NaN : Number(value));

const toOptions = (choices, labels) =>
  choices.map((choice) => ({ value: choice, label: labels[choice] ?? String(choice) }));

const widthRange = (entries) =>
  [
    ...new Set(
      entries.map(({ definition }) => `${definition.minWidth}–${definition.maxWidth}`)
    ),
  ].join(", ");

/**
 * Bounded "Kolom dan tampilan": known columns, row order, safe style enums, number
 * display and page setup. Draft edits do not touch the applied report until Terapkan
 * regenerates it.
 */
export const InventoryValueReportModificationDialog = ({
  open,
  options,
  layout,
  sort,
  applying,
  onCancel,
  onApply,
  onSave,
}) => {
  const [draft, setDraft] = useState(() => cloneLayout(layout));
  const [draftSort, setDraftSort] = useState(sort);
  const [tab, setTab] = useState("columns");
  const [applyError, setApplyError] = useState(null);

  const bounds = options.layoutOptions;
  const errors = useMemo(() => validateLayout(draft, options), [draft, options]);
  const isValid = Object.keys(errors).length === 0;

  const groups = useMemo(
    () =>
      getOrderedColumns(draft, options).reduce((result, entry) => {
        const last = result[result.length - 1];
        if (last?.block === entry.definition.block) {
          last.entries.push(entry);
        } else {
          result.push({ block: entry.definition.block, entries: [entry] });
        }
        return result;
      }, []),
    [draft, options]
  );

  const setField = (key, value) =>
    setDraft((current) => ({ ...cloneLayout(current), [key]: value }));

  const handleApply = async () => {
    setApplyError(null);
    const result = await onApply(draft, draftSort);
    if (result?.status === "error") {
      setApplyError(result.error?.message ?? "Tampilan gagal diterapkan.");
    }
  };

  return (
    <Dialog open={open} onClose={applying ? undefined : onCancel} fullWidth maxWidth="md">
      <CustomDialogTitle title="Kolom dan Tampilan" onClose={applying ? undefined : onCancel} />
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="info">
            Menerapkan tampilan juga memperbarui data laporan. Filter dan cakupan
            laporan tidak berubah.
          </Alert>
          {errors.general && <Alert severity="warning">{errors.general}</Alert>}
          {applyError && (
            <Alert severity="error">
              {applyError} Laporan sebelumnya tetap ditampilkan.
            </Alert>
          )}

          <Tabs
            value={tab}
            onChange={(_, value) => setTab(value)}
            variant="scrollable"
          >
            <Tab value="columns" label="Kolom" />
            <Tab value="appearance" label="Urutan dan tampilan" />
            <Tab value="numbers" label="Angka" />
            <Tab value="page" label="Halaman" />
          </Tabs>

          {tab === "columns" && (
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                Kolom hanya dapat dipindah di dalam kelompoknya. Nama Barang atau
                Kode Barang dan minimal satu kolom saldo harus tampil. Lebar
                bersifat relatif: tabel selalu diskalakan agar pas dengan lebar
                kertas.
              </Typography>
              <Box sx={{ ...COLUMN_GRID_SX, display: { xs: "none", sm: "grid" } }}>
                {["Kolom", "Label", "Lebar (mm)", "Urutan"].map((caption) => (
                  <Typography key={caption} variant="caption" color="text.secondary">
                    {caption}
                  </Typography>
                ))}
              </Box>
              {groups.map(({ block, entries }) => (
                <Stack key={block} spacing={1}>
                  <Typography variant="subtitle2">
                    {BLOCK_TITLES[block]}
                    <Typography component="span" variant="caption" color="text.secondary">
                      {` · lebar ${widthRange(entries)} mm`}
                    </Typography>
                  </Typography>
                  {entries.map(({ definition, column }) => {
                    const labelError = errors[`columns.${column.id}.label`];
                    const widthError = errors[`columns.${column.id}.width`];
                    return (
                      <Box key={column.id} sx={COLUMN_GRID_SX}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={column.visible}
                              onChange={(event) =>
                                setDraft((current) =>
                                  updateLayoutColumn(current, column.id, {
                                    visible: event.target.checked,
                                  })
                                )
                              }
                            />
                          }
                          label={definition.defaultLabel}
                        />
                        <CustomInputField
                          value={column.label}
                          onChange={(event) =>
                            setDraft((current) =>
                              updateLayoutColumn(current, column.id, {
                                label: event.target.value,
                              })
                            )
                          }
                          error={Boolean(labelError)}
                          helperText={labelError}
                          inputProps={{
                            maxLength: bounds.labelMaxLength,
                            "aria-label": `Label ${definition.defaultLabel}`,
                          }}
                        />
                        <CustomInputField
                          value={Number.isNaN(column.width) ? "" : column.width}
                          onChange={(event) =>
                            setDraft((current) =>
                              updateLayoutColumn(current, column.id, {
                                width: toInteger(event.target.value),
                              })
                            )
                          }
                          error={Boolean(widthError)}
                          helperText={widthError}
                          InputProps={{ type: "number" }}
                          inputProps={{
                            min: definition.minWidth,
                            max: definition.maxWidth,
                            step: 10,
                            "aria-label": `Lebar ${definition.defaultLabel}`,
                          }}
                        />
                        <Stack direction="row">
                          <IconButton
                            aria-label={`Naikkan ${definition.defaultLabel}`}
                            disabled={!canMoveColumn(draft, options, column.id, "up")}
                            onClick={() =>
                              setDraft((current) =>
                                moveLayoutColumn(current, options, column.id, "up")
                              )
                            }
                          >
                            <ArrowUpward fontSize="small" />
                          </IconButton>
                          <IconButton
                            aria-label={`Turunkan ${definition.defaultLabel}`}
                            disabled={!canMoveColumn(draft, options, column.id, "down")}
                            onClick={() =>
                              setDraft((current) =>
                                moveLayoutColumn(current, options, column.id, "down")
                              )
                            }
                          >
                            <ArrowDownward fontSize="small" />
                          </IconButton>
                        </Stack>
                      </Box>
                    );
                  })}
                </Stack>
              ))}
            </Stack>
          )}

          {tab === "appearance" && (
            <Grid container spacing={2}>
              <Grid size={12}>
                <CustomSelect
                  label="Urutkan baris"
                  options={sortOptions({
                    sortFields: options.sortFields ?? Object.keys(SORT_FIELD_LABELS),
                    sortDirections:
                      options.sortDirections ?? Object.keys(SORT_DIRECTION_LABELS),
                  })}
                  value={`${draftSort.field}:${draftSort.direction}`}
                  onChange={(event) => {
                    const [field, direction] = event.target.value.split(":");
                    setDraftSort({ field, direction });
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <CustomSelect
                  label="Huruf"
                  options={toOptions(bounds.fontFamilies, FONT_FAMILY_LABELS)}
                  value={draft.fontFamily}
                  onChange={(event) => setField("fontFamily", event.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <CustomInputField
                  label="Ukuran huruf (pt)"
                  value={Number.isNaN(draft.fontSize) ? "" : draft.fontSize}
                  onChange={(event) => setField("fontSize", toInteger(event.target.value))}
                  error={Boolean(errors.fontSize)}
                  helperText={
                    errors.fontSize ?? "Berlaku sama di layar dan saat dicetak."
                  }
                  InputProps={{ type: "number" }}
                  inputProps={{ min: bounds.fontSize.min, max: bounds.fontSize.max }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <CustomSelect
                  label="Kerapatan"
                  options={toOptions(bounds.densities, DENSITY_LABELS)}
                  value={draft.density}
                  onChange={(event) => setField("density", event.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <CustomSelect
                  label="Tema judul kolom"
                  options={toOptions(bounds.headerThemes, HEADER_THEME_LABELS)}
                  value={draft.headerTheme}
                  onChange={(event) => setField("headerTheme", event.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={draft.striped}
                      onChange={(event) => setField("striped", event.target.checked)}
                    />
                  }
                  label="Baris berselang warna"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={draft.boldHeaders}
                      onChange={(event) => setField("boldHeaders", event.target.checked)}
                    />
                  }
                  label="Judul kolom tebal"
                />
              </Grid>
            </Grid>
          )}

          {tab === "numbers" && (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <CustomSelect
                  label="Desimal nilai (IDR)"
                  options={toOptions(bounds.moneyDecimals, MONEY_DECIMAL_LABELS)}
                  value={draft.moneyDecimals}
                  onChange={(event) => setField("moneyDecimals", Number(event.target.value))}
                  helperText="Total tetap dihitung dari nilai sebelum pembulatan."
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <CustomSelect
                  label="Format angka negatif"
                  options={toOptions(bounds.negativeFormats, NEGATIVE_FORMAT_LABELS)}
                  value={draft.negativeFormat}
                  onChange={(event) => setField("negativeFormat", event.target.value)}
                />
              </Grid>
              <Grid size={12}>
                <Typography variant="body2" color="text.secondary">
                  Kuantitas selalu ditampilkan sebagai bilangan bulat dengan format
                  Indonesia.
                </Typography>
              </Grid>
            </Grid>
          )}

          {tab === "page" && (
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Stack spacing={2}>
                  <Typography variant="subtitle2">Pengaturan kertas</Typography>
                  <CustomSelect
                    label="Ukuran kertas"
                    options={paperOptions(bounds)}
                    value={draft.paper}
                    onChange={(event) => setField("paper", event.target.value)}
                  />
                  <FormControl>
                    <FormLabel sx={{ fontSize: "0.875rem" }}>Orientasi</FormLabel>
                    <RadioGroup
                      row
                      value={draft.orientation}
                      onChange={(event) => setField("orientation", event.target.value)}
                    >
                      {toOptions(bounds.orientations, ORIENTATION_LABELS).map(
                        ({ value, label }) => (
                          <FormControlLabel
                            key={value}
                            value={value}
                            control={<Radio />}
                            label={label}
                          />
                        )
                      )}
                    </RadioGroup>
                  </FormControl>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={draft.flexibleHeight}
                        onChange={(event) =>
                          setField("flexibleHeight", event.target.checked)
                        }
                      />
                    }
                    label="Tinggi kertas fleksibel"
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: -1.5 }}>
                    Dicetak sebagai satu halaman setinggi isi laporan, tanpa
                    pemisahan halaman.
                  </Typography>
                </Stack>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Stack spacing={2}>
                  <Typography variant="subtitle2">Margin halaman</Typography>
                  <Grid container spacing={2}>
                    {MARGIN_FIELDS.map(({ key, label }) => (
                      <Grid key={key} size={6}>
                        <CustomInputField
                          label={label}
                          value={Number.isNaN(draft[key]) ? "" : draft[key]}
                          onChange={(event) => setField(key, toInteger(event.target.value))}
                          error={Boolean(errors[key])}
                          helperText={errors[key]}
                          InputProps={{
                            type: "number",
                            endAdornment: <InputAdornment position="end">mm</InputAdornment>,
                          }}
                          inputProps={{
                            min: bounds.marginMm.min,
                            max: bounds.marginMm.max,
                          }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Stack>
              </Grid>
              <Grid size={12}>
                <CustomInputField
                  label="Judul laporan"
                  value={draft.title}
                  onChange={(event) => setField("title", event.target.value)}
                  error={Boolean(errors.title)}
                  helperText={errors.title ?? "Kosongkan untuk judul default."}
                  inputProps={{ maxLength: bounds.titleMaxLength }}
                />
              </Grid>
              <Grid size={12}>
                <CustomInputField
                  label="Catatan kaki"
                  multiline
                  rows={2}
                  value={draft.footerNote}
                  onChange={(event) => setField("footerNote", event.target.value)}
                  error={Boolean(errors.footerNote)}
                  helperText={errors.footerNote}
                  inputProps={{ maxLength: bounds.footerNoteMaxLength }}
                />
              </Grid>
              <Grid size={12}>
                <Alert severity="info">
                  Pratinjau menampilkan laporan persis seperti kertas yang dipilih.
                  Periode, cakupan gudang, filter, dan peringatan kualitas nilai
                  selalu dicetak dan tidak dapat disembunyikan.
                </Alert>
              </Grid>
            </Grid>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
        <Stack direction="row" spacing={1}>
          <Button
            onClick={() => {
              setDraft(cloneLayout(options.defaultLayout));
              setDraftSort(DEFAULT_SORT);
            }}
            disabled={applying}
          >
            Reset default
          </Button>
          <Button onClick={() => onSave(draft)} disabled={!isValid || applying}>
            Simpan tampilan di browser ini
          </Button>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button onClick={onCancel} disabled={applying}>
            Batal
          </Button>
          <Button
            variant="contained"
            onClick={handleApply}
            disabled={!isValid || applying}
          >
            {applying ? "Menerapkan…" : "Terapkan"}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

InventoryValueReportModificationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  options: PropTypes.object.isRequired,
  layout: PropTypes.object.isRequired,
  sort: PropTypes.shape({
    field: PropTypes.string.isRequired,
    direction: PropTypes.string.isRequired,
  }).isRequired,
  applying: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};
